import { addDays, endOfDay, format, startOfDay } from "date-fns";
import { getDateBasedLabel } from "../../../services/adsCampaigns";
import { aggregatePortfolioTimeSeries } from "./aggregatePortfolioTimeSeries";
import {
  getBreakdownBatchSize,
  groupForecastDailyValuesByBreakdown,
  groupGoalsHistoricalRowsByBreakdown,
} from "./groupPortfolioTimeSeriesByBreakdown";
import {
  getGlobalReportGoalsProjectionWindow,
  GLOBAL_REPORT_GOALS_PROJECTION_FORECAST_DAYS,
} from "./getGlobalReportGoalsDateRanges";
import {
  GLOBAL_REPORT_PROJECTION_ACTUAL_COLOR,
  GLOBAL_REPORT_PROJECTION_FORECAST_COLOR,
} from "./globalReportChartPalette";

export const computeAverageDailyDelta = (deltas) => {
  if (!deltas.length) {
    return 0;
  }

  const positive = deltas.filter((d) => d > 0);
  const pool = positive.length > 0 ? positive : deltas;
  const sum = pool.reduce((acc, d) => acc + d, 0);
  return sum / pool.length;
};

const isBucketInProjectionWindow = (bucketMeta, window) => {
  if (!window || !bucketMeta?.startDateTime) {
    return false;
  }

  const bucketDay = startOfDay(new Date(bucketMeta.startDateTime)).getTime();
  return (
    bucketDay >= window.windowStartTime && bucketDay <= window.windowEndTime
  );
};

const emptyProjectionResult = () => ({
  chartData: [],
  chartConfig: {},
  hasData: false,
  avgDailyDelta: 0,
  achievedInWindow: 0,
  historicalDaily: [],
  projectionWindow: null,
  hasHistoricalActivity: false,
});

/**
 * Extracts daily mainAction history for goals projection (API + charts).
 */
export const extractGlobalReportGoalsHistoricalSeries = ({
  campaigns = {},
  statistics = null,
  dispatch,
  startDateTime,
  endDateTime,
}) => {
  const projectionWindow = getGlobalReportGoalsProjectionWindow({
    startDateTime,
    endDateTime,
  });

  if (!projectionWindow) {
    return { ...emptyProjectionResult(), projectionWindow: null };
  }

  const { portfolioBuckets, buckets } = aggregatePortfolioTimeSeries({
    campaigns,
    statistics,
    dispatch,
  });

  const filteredSeries = buckets
    .map((bucketMeta, index) => ({
      bucketMeta,
      bucket: portfolioBuckets[index],
    }))
    .filter(({ bucketMeta }) =>
      isBucketInProjectionWindow(bucketMeta, projectionWindow)
    );

  if (!filteredSeries.length) {
    return { ...emptyProjectionResult(), projectionWindow };
  }

  const referenceDate = projectionWindow.referenceDate;
  const today = startOfDay(referenceDate);

  const bucketRows = filteredSeries.map(({ bucketMeta, bucket }) => ({
    startDateTime: bucketMeta?.startDateTime,
    endDateTime: bucketMeta?.endDateTime,
    date: bucketMeta?.startDateTime
      ? startOfDay(new Date(bucketMeta.startDateTime))
      : null,
    delta: Number(bucket?.mainAction || 0),
  }));

  const historicalRows = bucketRows.filter(
    (row) => row.date && row.date.getTime() <= endOfDay(referenceDate).getTime()
  );

  if (!historicalRows.length) {
    return { ...emptyProjectionResult(), projectionWindow };
  }

  const deltas = historicalRows.map((row) => row.delta);
  const achievedInWindow = deltas.reduce((sum, d) => sum + d, 0);
  const avgDailyDelta = computeAverageDailyDelta(deltas);
  const hasHistoricalActivity = deltas.some((d) => d > 0);

  const historicalDaily = historicalRows
    .filter((row) => row.date)
    .map((row) => ({
      date: format(row.date, "yyyy-MM-dd"),
      results: row.delta,
    }));

  return {
    historicalRows,
    historicalDaily,
    deltas,
    achievedInWindow,
    avgDailyDelta,
    hasHistoricalActivity,
    projectionWindow,
    today,
    referenceDate,
  };
};

const buildChartConfig = () => ({
  dailyActual: {
    label: "dailyActual",
    color: GLOBAL_REPORT_PROJECTION_ACTUAL_COLOR,
    isVisible: true,
  },
  dailyProjected: {
    label: "dailyProjected",
    color: GLOBAL_REPORT_PROJECTION_FORECAST_COLOR,
    isVisible: true,
    strokeDasharray: "6 4",
  },
});

const isRowPeriodContainingToday = (row, today) => {
  if (!row?.startDateTime || !row?.endDateTime) {
    return false;
  }

  const periodStart = startOfDay(new Date(row.startDateTime)).getTime();
  const periodEnd = endOfDay(new Date(row.endDateTime)).getTime();
  const todayTime = startOfDay(today).getTime();

  return todayTime >= periodStart && todayTime <= periodEnd;
};

/**
 * Builds daily actual line only (no forecast segment).
 */
export const buildGlobalReportGoalsActualChart = ({
  historicalRows,
  today,
  breakdown = "daily",
}) => {
  const chartData = [];

  historicalRows.forEach((row) => {
    const label =
      getDateBasedLabel(breakdown, row.startDateTime, row.endDateTime) ||
      String(chartData.length + 1);
    const isToday = isRowPeriodContainingToday(row, today);
    const dailyValue = row.delta;

    chartData.push({
      label,
      dailyActual: dailyValue,
      dailyProjected: isToday ? dailyValue : null,
      isProjected: false,
      isToday,
    });
  });

  const lastPoint = chartData[chartData.length - 1];
  if (lastPoint && lastPoint.dailyProjected == null) {
    lastPoint.dailyProjected = lastPoint.dailyActual;
    lastPoint.isToday = true;
  }

  return { chartData };
};

export const appendLinearForecastToChart = ({
  chartData,
  today,
  avgDailyDelta,
  forecastDays,
  breakdown = "daily",
}) => {
  const batchSize = getBreakdownBatchSize(breakdown);

  if (batchSize === 1) {
    for (let day = 1; day <= forecastDays; day += 1) {
      const futureDate = addDays(today, day);
      const label =
        getDateBasedLabel(
          breakdown,
          futureDate.getTime(),
          endOfDay(futureDate).getTime()
        ) || `+${day}`;

      chartData.push({
        label,
        dailyActual: null,
        dailyProjected: Math.round(avgDailyDelta * 100) / 100,
        isProjected: true,
        isToday: false,
      });
    }

    return chartData;
  }

  for (let day = 1; day <= forecastDays; day += batchSize) {
    const startDay = day;
    const endDay = Math.min(day + batchSize - 1, forecastDays);
    const daysInBatch = endDay - startDay + 1;
    const futureStart = addDays(today, startDay);
    const futureEnd = addDays(today, endDay);
    const label =
      getDateBasedLabel(
        breakdown,
        futureStart.getTime(),
        endOfDay(futureEnd).getTime()
      ) || `+${startDay}`;

    chartData.push({
      label,
      dailyActual: null,
      dailyProjected: Math.round(avgDailyDelta * daysInBatch * 100) / 100,
      isProjected: true,
      isToday: false,
    });
  }

  return chartData;
};

export const appendAiForecastToChart = ({
  chartData,
  today,
  forecastDailyResults = [],
  breakdown = "daily",
}) => {
  const groupedForecast = groupForecastDailyValuesByBreakdown({
    forecastDailyResults,
    breakdown,
  });

  groupedForecast.forEach(({ value, startDayOffset, endDayOffset }) => {
    const futureStart = addDays(today, startDayOffset);
    const futureEnd = addDays(today, endDayOffset);
    const label =
      getDateBasedLabel(
        breakdown,
        futureStart.getTime(),
        endOfDay(futureEnd).getTime()
      ) || `+${startDayOffset}`;

    chartData.push({
      label,
      dailyActual: null,
      dailyProjected: Math.round(Number(value) * 100) / 100,
      isProjected: true,
      isToday: false,
    });
  });

  return chartData;
};

/**
 * Daily mainAction (actual) + forecast from main-report unified statistics.
 * Uses at most 30 days ending at report endDateTime, or fewer if the selected range is shorter.
 * @param {number[] | null | undefined} forecastDailyResults - AI daily increments; omit for linear fallback
 */
export const buildGlobalReportGoalsProjectionDataset = ({
  campaigns = {},
  statistics = null,
  dispatch,
  breakdown = "daily",
  startDateTime,
  endDateTime,
  forecastDays = GLOBAL_REPORT_GOALS_PROJECTION_FORECAST_DAYS,
  forecastDailyResults = null,
}) => {
  const extracted = extractGlobalReportGoalsHistoricalSeries({
    campaigns,
    statistics,
    dispatch,
    startDateTime,
    endDateTime,
  });

  const {
    historicalRows,
    historicalDaily,
    achievedInWindow,
    avgDailyDelta,
    hasHistoricalActivity,
    projectionWindow,
    today,
  } = extracted;

  if (!projectionWindow || !historicalRows?.length) {
    return {
      ...emptyProjectionResult(),
      historicalDaily: historicalDaily || [],
    };
  }

  const displayHistoricalRows = groupGoalsHistoricalRowsByBreakdown({
    historicalRows,
    breakdown,
  });

  const { chartData } = buildGlobalReportGoalsActualChart({
    historicalRows: displayHistoricalRows,
    today,
    breakdown,
  });

  const useAiForecast =
    Array.isArray(forecastDailyResults) &&
    forecastDailyResults.length === forecastDays;

  if (useAiForecast) {
    appendAiForecastToChart({
      chartData,
      today,
      forecastDailyResults,
      breakdown,
    });
  } else {
    appendLinearForecastToChart({
      chartData,
      today,
      avgDailyDelta,
      forecastDays,
      breakdown,
    });
  }

  const hasActivity = hasHistoricalActivity || chartData.length > 0;

  return {
    chartData,
    chartConfig: buildChartConfig(),
    hasData: hasActivity,
    avgDailyDelta,
    achievedInWindow,
    historicalDaily,
    hasHistoricalActivity,
  };
};

export default buildGlobalReportGoalsProjectionDataset;
